import React from "react";
import { useTranslation } from "react-i18next";
import { JointBill } from "@features/JointBillList/components/JointBillPeriod/components/JointBill/JointBill";
import { JointBillContext } from "@features/JointBillList/utils";
import { JointBillTableSkeleton } from "@features/skeletons/Bills/JointBillTableSkeleton/JointBillTableSkeleton";
import { Button } from "@components/Button";
import { Notification } from "@components/Notification/Notification";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { useUpdateHouseBillsSelectionMutation } from "@services/bills/mutations";
import { useNonProfitProvidersQuery } from "@services/nonProfits/queries";
import { useJointBillServicePeriodDataQuery } from "@services/overview/queries";
import {
  PeriodState,
  SelectionState,
  type IOverviewJointBillService,
} from "@services/overview/types";
import { usePoaJbsDataQuery } from "@services/powersOfAttorney/queries";

interface IJointBillPeriodProps {
  jointBillService: IOverviewJointBillService;
  periodId: number;
  isSummaryMode?: boolean;
  orderId: number;
}

export const JointBillPeriod: React.FC<IJointBillPeriodProps> = ({
  jointBillService,
  periodId,
  isSummaryMode,
  orderId,
}) => {
  const { t } = useTranslation();
  const { jointBillServiceId } = jointBillService;

  const {
    isLoading: isPeriodDataLoading,
    data: periodData,
    isError,
    refetch,
    isFetching,
  } = useJointBillServicePeriodDataQuery({
    jointBillServiceId,
    periodId,
  });

  const { isNonProfitProvidersEnabled, isPoaEnabled } = useFeatureConfig();
  const { data: nonProfitProviders, isLoading: isNonProfitProvidersLoading } =
    useNonProfitProvidersQuery(
      jointBillServiceId,
      isNonProfitProvidersEnabled && !isSummaryMode,
    );

  const { isLoading: isPoaJbsDataLoading, data: poaJbsData } =
    usePoaJbsDataQuery(isPoaEnabled);

  const poaSpRequests =
    poaJbsData?.find((j) => j.jointBillServiceId === jointBillServiceId)
      ?.serviceProviderRequests ?? [];

  const {
    mutateAsync: updateJointBillServiceSelection,
    isPending: isBillSelectionLoading,
  } = useUpdateHouseBillsSelectionMutation({ jointBillServiceId, periodId });

  const updateJointBillServiceCheckboxSelection = async () => {
    await updateJointBillServiceSelection({
      isSelected: periodData?.selectionState !== SelectionState.SELECTED,
      periodId: periodId,
      jointBillServiceId,
    });
  };

  if (
    isPeriodDataLoading ||
    isNonProfitProvidersLoading ||
    isPoaJbsDataLoading
  ) {
    return <JointBillTableSkeleton isSummaryMode={isSummaryMode} />;
  }

  if (isError) {
    return (
      <Notification
        variant="big"
        title={jointBillService.displayName ?? t("common.defaultAddress")}
        description={t("bill.errors.periodLoadingFailed.description")}
        color="red"
        imagePath=""
        button={
          <Button onClick={() => refetch()} disabled={isFetching}>
            {t("bill.errors.periodLoadingFailed.action")}
          </Button>
        }
      />
    );
  }

  if (!periodData) return null;

  const periodState = periodData?.jointBillServicePeriodState;

  const isArchiveMode =
    periodState !== PeriodState.Ready &&
    periodState !== PeriodState.PartiallyPaid &&
    periodState !== PeriodState.PartiallyPaidReady &&
    periodState !== PeriodState.Waiting;

  const otherUsers = jointBillService?.otherUsers?.filter(
    (otherUser) => !otherUser.isOwner,
  );

  return (
    <JointBillContext.Provider
      value={{
        isSummaryMode: !!isSummaryMode,
        isArchiveMode,
        nonProfitProviders,
        poaSpRequests,
        jointBillServiceProviderBillPlanId:
          periodData.jointBillServiceProviderBillPlanId,
      }}
    >
      <JointBill
        orderId={orderId}
        jointBillService={jointBillService}
        periodData={periodData}
        periodId={periodId}
        otherUsers={otherUsers}
        jointBillSelectionState={periodData.selectionState}
        isJointBillCheckboxDisabled={!periodData.selectionStateCanBeChanged}
        isJointBillCheckboxLoading={isBillSelectionLoading}
        onJointBillCheckboxClick={updateJointBillServiceCheckboxSelection}
      />
    </JointBillContext.Provider>
  );
};
